import { Drawer as MuiDrawer, capitalize } from '@mui/material';
import clsx from 'clsx';

import { drawerCssVars } from './cssVars';
import { DrawerProps, DrawerSize } from './types';
import { WithRef } from '../../types';
import { mergeSx, normalizePx } from '../../utils';

const getSpacingBySize = (size: DrawerSize): 'compact' | 'regular' => {
  switch (size) {
    case 'xxsmall':
    case 'xsmall':
    case 'small':
      return 'compact';
    default:
      return 'regular';
  }
};

const horizontalPaddingBySize = {
  compact: 24,
  regular: 32,
};

const verticalFooterPaddingBySize = {
  compact: 12,
  regular: 16,
};

export const Drawer = ({
  ref,
  color = 'primary',
  size = 'medium',
  spacing = getSpacingBySize(size),
  disableCloseOnBackdropClick,
  onClose,
  children,
  PaperProps,
  testId = 'ds-drawer',
  ...rest
}: WithRef<HTMLDivElement, DrawerProps>) => {
  const normalizedCssVars = drawerCssVars.setValues({
    horizontalPadding: normalizePx(horizontalPaddingBySize[spacing]),
    verticalFooterPadding: normalizePx(verticalFooterPaddingBySize[spacing]),
  });

  return (
    <MuiDrawer
      ref={ref}
      anchor="right"
      // Not setting this causes page to scroll when drawer is closed in chrome
      // I guess it can be set globally
      // https://github.com/mui/material-ui/issues/10756#issuecomment-375062360
      disableRestoreFocus
      {...rest}
      PaperProps={{
        component: 'aside',
        ...PaperProps,
        className: clsx(
          'DS-Drawer-root',
          `DS-Drawer-color${capitalize(color)}`,
          !!size && `DS-Drawer-size${capitalize(size)}`,
          PaperProps?.className
        ),
        sx: mergeSx(PaperProps?.sx, normalizedCssVars),
      }}
      onClose={(event, reason) => {
        if (disableCloseOnBackdropClick && reason === 'backdropClick') {
          return;
        }
        onClose?.(event, reason);
      }}
      data-testid={testId}
    >
      {children}
    </MuiDrawer>
  );
};

Drawer.displayName = 'Drawer';
